/**
 * Configurações › Cadastros jurídicos:
 *  - Campos dos formulários: obrigatório / opcional / oculto;
 *  - Listas do processo: fases, situações, instâncias e sistemas (renomear atualiza os processos).
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useFieldsConfig, type FieldMode } from "@/lib/fields";
import { Card, ErrorBox, Loading, useAct } from "@/ui/kit";
import { navigate } from "@/lib/router";
import { confirmar } from "@/ui/feedback";

const MODES: Array<{ key: FieldMode; label: string }> = [{ key: "obrigatorio", label: "Obrigatório" }, { key: "opcional", label: "Opcional" }, { key: "oculto", label: "Oculto" }];


export function FieldSettings(): JSX.Element {
  const { data, error } = useFieldsConfig();
  const [cfg, setCfg] = useState<Record<string, Record<string, FieldMode>> | null>(null);
  const [form, setForm] = useState("pessoa");
  useEffect(() => { if (data && !cfg) setCfg(data.config); }, [data, cfg]);
  const save = useAct(() => api.put("/config/campos", { config: cfg }), [["config-campos"]], undefined, "Campos salvos");
  if (error) return <ErrorBox error={error} />;
  if (!data || !cfg) return <Loading />;
  const def = data.forms.find((f) => f.key === form)!;
  const changed = JSON.stringify(cfg) !== JSON.stringify(data.config);
  return (
    <div className="stack gap-16">
      <Card title="Campos dos formulários" actions={<button className="btn primary" disabled={!changed || save.isPending} onClick={() => save.mutate()}>Salvar</button>}>
        <div className="muted small" style={{ marginBottom: 10 }}>
          Escolha o que é obrigatório, opcional ou oculto em cada cadastro. Vale na tela e no servidor (inclusive importação e API). Campos com cadeado o sistema precisa para funcionar.
          {" "}Fases, situações, instâncias e sistemas ficam em <button className="link-btn" onClick={() => navigate("/configuracoes?aba=listas")}>Listas do processo</button>.
        </div>
        <div className="fin-subs" style={{ marginBottom: 12 }}>
          {data.forms.map((f) => <button key={f.key} className={`fin-sub ${f.key === form ? "active" : ""}`} onClick={() => setForm(f.key)}>{f.label}</button>)}
        </div>
        <table className="mini-table" style={{ width: "100%" }}>
          <thead><tr><th style={{ textAlign: "left" }}>Campo</th><th style={{ textAlign: "left" }}>Situação</th></tr></thead>
          <tbody>{def.fields.map((fd) => (
            <tr key={fd.key}>
              <td>{fd.label}{fd.locked && <div className="muted small">🔒 {fd.locked}</div>}</td>
              <td>
                {fd.locked ? <span className="muted">Obrigatório</span> : (
                  <div className="seg">
                    {MODES.map((m) => <button key={m.key} className={cfg[form]?.[fd.key] === m.key ? "active" : ""} onClick={() => setCfg({ ...cfg, [form]: { ...cfg[form], [fd.key]: m.key } })}>{m.label}</button>)}
                  </div>
                )}
              </td>
            </tr>
          ))}</tbody>
        </table>
        <ErrorBox error={save.error} />
      </Card>
    </div>
  );
}

type ListKey = "fases" | "situacoes" | "instancias" | "sistemas";
const LISTS: Array<{ key: ListKey; label: string; hint: string; max: number }> = [
  { key: "fases", label: "Fases", hint: "Ex.: Conhecimento, Recursal, Cumprimento de sentença.", max: 64 },
  { key: "situacoes", label: "Situações", hint: "Ex.: Ativo, Suspenso, Arquivado.", max: 64 },
  { key: "instancias", label: "Instâncias", hint: "A busca pelo CNJ sugere a instância automaticamente.", max: 64 },
  { key: "sistemas", label: "Sistemas (PJe, eproc…)", hint: "A busca pelo CNJ sugere o sistema automaticamente.", max: 32 },
];
interface Item { orig: string | null; name: string }

/** Listas do processo: cada item numa linha; renomear atualiza os processos que usavam o nome antigo. */
export function ProcessListsSettings(): JSX.Element {
  const { data, error } = useFieldsConfig();
  const [lists, setLists] = useState<Record<ListKey, Item[]> | null>(null);
  const init = () => data && setLists(Object.fromEntries(LISTS.map((l) => [l.key, (data.lists[l.key] ?? []).map((x: string) => ({ orig: x, name: x }))])) as Record<ListKey, Item[]>);
  useEffect(() => { if (data && !lists) init(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [data, lists]);
  const save = useAct(() => {
    const out = Object.fromEntries(LISTS.map((l) => [l.key, lists![l.key].map((x) => x.name.trim()).filter(Boolean)]));
    const renames = Object.fromEntries(LISTS.map((l) => [l.key, Object.fromEntries(lists![l.key].filter((x) => x.orig && x.name.trim() && x.name.trim() !== x.orig).map((x) => [x.orig!, x.name.trim()]))]));
    return api.put("/config/campos", { lists: out, renames });
  }, [["config-campos"]], () => setLists(null), "Listas salvas");
  if (error) return <ErrorBox error={error} />;
  if (!data || !lists) return <Loading />;
  const changed = LISTS.some((l) => JSON.stringify(lists[l.key].map((x) => x.name.trim())) !== JSON.stringify(data.lists[l.key] ?? []));
  const renamed = LISTS.flatMap((l) => lists[l.key].filter((x) => x.orig && x.name.trim() && x.name.trim() !== x.orig).map((x) => `${x.orig} → ${x.name.trim()}`));
  const upd = (k: ListKey, items: Item[]) => setLists({ ...lists, [k]: items });
  const move = (k: ListKey, i: number, d: -1 | 1) => { const a = [...lists[k]]; const j = i + d; if (j < 0 || j >= a.length) return; [a[i], a[j]] = [a[j]!, a[i]!]; upd(k, a); };
  const doSave = async () => {
    if (renamed.length && !(await confirmar(`Renomear também nos processos que já usam:\n${renamed.join("\n")}`, { confirm: "Salvar" }))) return;
    save.mutate();
  };
  return (
    <Card title="Listas do processo" actions={<div className="row gap-8">
      {changed && <button className="btn" onClick={() => setLists(null)}>Desfazer</button>}
      <button className="btn primary" disabled={!changed || save.isPending} onClick={() => void doSave()}>Salvar</button>
    </div>}>
      <div className="stack gap-12">
        <div className="muted small">
          Opções que aparecem no cadastro do processo. <b>Renomear</b> um item atualiza os processos que usavam o nome antigo. <b>Remover</b> só tira da lista:
          os processos que já usam continuam com o nome antigo. A ordem aqui é a ordem das opções. Também dá para editar em massa pela Planilha mestre.
        </div>
        <div className="grid cols-2">
          {LISTS.map((l) => (
            <div key={l.key} className="stack gap-4 list-editor">
              <div className="fld-label">{l.label}</div>
              {lists[l.key].map((x, i) => (
                <div key={i} className="row gap-4 nowrap">
                  <input className="input" maxLength={l.max} value={x.name} onChange={(e) => upd(l.key, lists[l.key].map((y, j) => (j === i ? { ...y, name: e.target.value } : y)))} />
                  <button className="btn small ghost" title="Subir" disabled={i === 0} onClick={() => move(l.key, i, -1)}>↑</button>
                  <button className="btn small ghost" title="Descer" disabled={i === lists[l.key].length - 1} onClick={() => move(l.key, i, 1)}>↓</button>
                  <button className="btn small ghost danger" title="Tirar da lista" disabled={lists[l.key].length <= 1} onClick={() => upd(l.key, lists[l.key].filter((_, j) => j !== i))}>✕</button>
                </div>
              ))}
              <button className="btn small" style={{ alignSelf: "flex-start" }} onClick={() => upd(l.key, [...lists[l.key], { orig: null, name: "" }])}>+ Adicionar</button>
              <div className="muted small">{l.hint}</div>
            </div>
          ))}
        </div>
        <ErrorBox error={save.error} />
      </div>
    </Card>
  );
}
